import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessagesSquare, Send, X, Loader2 } from "lucide-react";
import { API } from "../lib/store";

const GREETING =
  "Hello! I'm the Udat Marine AI assistant. Ask me about our services, coverage area, or how to reach our 24/7 duty officer.";

const cleanReply = (text) =>
  text
    .replace(/\*\*/g, "")
    .replace(/^\s*[*-]\s+/gm, "• ")
    .replace(/^#+\s+/gm, "");
const QUICK = [
  "What services do you offer?",
  "Do you deliver fresh water outside the port limit?",
  "I need a quote",
];

const getSessionId = () => {
  let id = localStorage.getItem("udat_chat_session");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("udat_chat_session", id);
  }
  return id;
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    const history = msgs.slice(-10).filter((m) => m.text !== GREETING);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionRef.current,
          message,
          history,
        }),
      });
      if (!res.ok || !res.body) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || "Chat unavailable — please WhatsApp us at +6017-7529304.");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let finished = false;
      while (!finished) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") {
            finished = true;
            break;
          }
          const parsed = JSON.parse(payload);
          if (parsed.delta) {
            setMsgs((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                text: copy[copy.length - 1].text + parsed.delta,
              };
              return copy;
            });
          }
          if (parsed.error) {
            setMsgs((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], text: parsed.error };
              return copy;
            });
            finished = true;
          }
        }
      }
    } catch (err) {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          text: err?.message || "Something went wrong — please try again.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-launcher"
        onClick={() => setOpen(!open)}
        aria-label="Open AI chat"
        className="group fixed bottom-[104px] right-5 z-40 flex items-center gap-3"
      >
        <span className="pointer-events-none hidden border border-line bg-ink/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-100 opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:opacity-100 md:block">
          Ask our AI
        </span>
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-[0_12px_32px_rgba(217,35,35,0.35)] transition-transform duration-300 group-hover:scale-105">
          {open ? <X size={22} /> : <MessagesSquare size={22} />}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-[184px] right-5 z-40 flex h-[480px] w-[360px] max-w-[calc(100vw-2.5rem)] flex-col border border-line bg-surface shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <div>
                <div className="font-display text-sm uppercase tracking-[0.12em] text-slate-100">
                  Udat Marine AI
                </div>
                <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-steel">
                  Powered by Gemini · answers instantly
                </div>
              </div>
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {msgs.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-brand text-white"
                      : "border border-line bg-ink/60 text-slate-200"
                  }`}
                >
                  {m.role === "assistant" ? cleanReply(m.text) : m.text}
                  {streaming && i === msgs.length - 1 && m.role === "assistant" && !m.text && (
                    <Loader2 size={14} className="animate-spin text-steel" />
                  )}
                </div>
              ))}
              {msgs.length === 1 && (
                <div className="flex flex-col gap-2 pt-1">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      data-testid={`chat-quick-${q.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => send(q)}
                      className="w-fit border border-line px-3 py-1.5 text-left text-xs text-steel transition-colors hover:border-brand hover:text-brand"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex gap-2 border-t border-line p-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our services..."
                className="flex-1 border border-line bg-transparent px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-brand"
              />
              <button
                data-testid="chat-send"
                type="submit"
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                className="flex w-11 items-center justify-center bg-brand text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
              >
                {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
